import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  btnDangerClass,
  btnSecondaryClass,
  btnPrimaryClass,
  alertErrorClass,
  alertInfoClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  formControlClass,
  formLabelClass,
} from "./dashboardStyles";
import {
  GetAccountDeletionStatusAction,
  RequestAccountDeletionAction,
  CancelAccountDeletionAction,
  logout,
} from "../../store/action";
import { notify, notifySaveError } from "../../utils/notify";

/**
 * The GDPR art. 17 erasure control, in Settings.
 *
 * Two states: no deletion pending (offer it, behind a typed confirmation), or
 * one pending (show the date and a way to stop it). The confirmation is
 * deliberately typed rather than a second "are you sure" button — this is the
 * one irreversible action in the product, and a modal you can dismiss by
 * clicking twice is not a decision.
 */
const CONFIRM_WORD = "DELETE";

const formatDate = (value, language) =>
  value
    ? new Date(value).toLocaleDateString(language === "ro" ? "ro-RO" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

const DeleteAccountPanel = () => {
  const { t, i18n } = useTranslation();
  const language = i18n?.language?.startsWith("ro") ? "ro" : "en";

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Errors are stored unresolved — a server message if there was one, otherwise
  // the key to translate at render time. Calling t() in here would put it in the
  // dependency list, and t is a fresh identity on every render: the effect below
  // would re-run each time, refetching in a loop and overwriting whatever the
  // user just did.
  const resolveError = (err, fallbackKey) => ({
    serverMessage: err?.response?.data?.message ?? null,
    key: fallbackKey,
  });

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);
      setStatus(await GetAccountDeletionStatusAction());
    } catch (err) {
      setError(resolveError(err, "Dashboard.Settings.deleteAccount.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // A failed request or cancellation goes to the snackbar rather than the card:
  // the request fails with the confirmation modal still open over the card, so
  // an alert inside it would sit where nobody can see it.
  const handleRequest = async () => {
    setSubmitting(true);
    try {
      await RequestAccountDeletionAction();
      setConfirmOpen(false);
      setConfirmText("");
      // The account is suspended from this moment. Staying signed in on a
      // dashboard that no longer represents anything usable would be
      // misleading, so end the session and let them read the email.
      logout(true);
    } catch (err) {
      notifySaveError(err, t("Dashboard.Settings.deleteAccount.requestFailed"));
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      setStatus(await CancelAccountDeletionAction());
      notify.success(t("Dashboard.Settings.deleteAccount.cancelled"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.Settings.deleteAccount.cancelFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div className={`${cardClass} delete-account-panel`}>
      <div className={cardHeaderClass}>
        <h3 className={cardTitleClass}>
          {t("Dashboard.Settings.deleteAccount.title")}
        </h3>
      </div>

      <div className={cardBodyClass}>
        {error && (
          <div className={`${alertErrorClass} mb-4`}>
            <i className="bi bi-exclamation-circle" aria-hidden="true" />
            {error.serverMessage || t(error.key)}
          </div>
        )}

        {status?.deletionPending ? (
          <>
            <div className={`${alertInfoClass} mb-4`}>
              <i className="bi bi-clock-history" aria-hidden="true" />
              <span>
                {t("Dashboard.Settings.deleteAccount.pending", {
                  date: formatDate(status.scheduledFor, language),
                  days: status.daysRemaining,
                })}
              </span>
            </div>

            <p className="m-0 mb-4 text-fluid-sm text-ink-500">
              {t("Dashboard.Settings.deleteAccount.pendingHelp")}
            </p>

            <button
              type="button"
              className={btnPrimaryClass}
              onClick={handleCancel}
              disabled={submitting}
            >
              {t("Dashboard.Settings.deleteAccount.cancelAction")}
            </button>
          </>
        ) : (
          <>
            <p className="m-0 mb-3 text-fluid-sm text-ink-500">
              {t("Dashboard.Settings.deleteAccount.description")}
            </p>

            <ul className="mb-4 space-y-1.5 pl-5">
              <li className="list-disc text-fluid-sm text-ink-500">
                {t("Dashboard.Settings.deleteAccount.pointGrace")}
              </li>
              <li className="list-disc text-fluid-sm text-ink-500">
                {t("Dashboard.Settings.deleteAccount.pointIrreversible")}
              </li>
              <li className="list-disc text-fluid-sm text-ink-500">
                {t("Dashboard.Settings.deleteAccount.pointInvoices")}
              </li>
            </ul>

            <button
              type="button"
              className={btnDangerClass}
              onClick={() => setConfirmOpen(true)}
            >
              <i className="bi bi-trash" aria-hidden="true" />
              {t("Dashboard.Settings.deleteAccount.requestAction")}
            </button>
          </>
        )}
      </div>

      {confirmOpen && (
        <div className={modalOverlayClass} onClick={() => setConfirmOpen(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            className={modalPanelClass}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={modalHeaderClass}>
              <h3 id="delete-account-title" className={cardTitleClass}>
                {t("Dashboard.Settings.deleteAccount.confirmTitle")}
              </h3>
              <button
                type="button"
                className={modalCloseClass}
                onClick={() => setConfirmOpen(false)}
                aria-label={t("Dashboard.Settings.deleteAccount.close")}
              >
                <i className="bi bi-x text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className={modalBodyClass}>
              {/* The consequences that are specific to *this* account come
                  first. A generic warning is ignored; "your business goes
                  offline" is not. */}
              {status?.ownedOrganizationName && (
                <div className={`${alertErrorClass} mb-4`}>
                  <i className="bi bi-shop" aria-hidden="true" />
                  <span>
                    {t("Dashboard.Settings.deleteAccount.warnOrganization", {
                      name: status.ownedOrganizationName,
                    })}
                  </span>
                </div>
              )}

              {status?.upcomingBookingCount > 0 && (
                <div className={`${alertErrorClass} mb-4`}>
                  <i className="bi bi-calendar-x" aria-hidden="true" />
                  <span>
                    {t("Dashboard.Settings.deleteAccount.warnBookings", {
                      count: status.upcomingBookingCount,
                    })}
                  </span>
                </div>
              )}

              {/* Cannot be detected reliably — a sole trader's business name is
                  often their own name, and that is personal data we would
                  otherwise leave published. */}
              {status?.ownedOrganizationName && (
                <p className="m-0 mb-4 text-fluid-sm text-ink-500">
                  {t("Dashboard.Settings.deleteAccount.warnBusinessNameIsPersonal")}
                </p>
              )}

              <p className="m-0 mb-4 text-fluid-sm text-ink-600">
                {t("Dashboard.Settings.deleteAccount.confirmBody")}
              </p>

              <label htmlFor="delete-account-confirm" className={formLabelClass}>
                {t("Dashboard.Settings.deleteAccount.confirmPrompt", {
                  word: CONFIRM_WORD,
                })}
              </label>
              <input
                id="delete-account-confirm"
                type="text"
                className={formControlClass}
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                autoComplete="off"
              />
            </div>

            <div className={modalFooterClass}>
              <button
                type="button"
                className={btnSecondaryClass}
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
              >
                {t("Dashboard.Settings.deleteAccount.keepAccount")}
              </button>
              <button
                type="button"
                className={btnDangerClass}
                onClick={handleRequest}
                disabled={submitting || confirmText.trim() !== CONFIRM_WORD}
              >
                {submitting
                  ? t("Dashboard.Settings.deleteAccount.submitting")
                  : t("Dashboard.Settings.deleteAccount.confirmAction")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountPanel;
